import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { routes } from "@/routes/utils";
import {
  useGetPropertyBlockByCommunityId,
  useGetPropertyBlocksZoneWise,
  useBulkDeletePropertyBlockMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { ROLE_BOM } from "@/lib/utils";

const usePropertyBlock = () => {
  const location = useLocation();
  const isOwner = location?.pathname?.includes("owner");
  const isMobile = useIsMobile();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [openBlockDialog, setOpenBlockDialog] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const masterDataPermission = useSelector((state: RootState) => state.currentData.masterDataPermission);
  const currentRole = useSelector((state: RootState) => state.currentData.currentRole);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);
  const isCamBom = location.pathname.startsWith("/cam") || location.pathname.startsWith("/bom") || (location.pathname.startsWith("/owner") && currentRole == ROLE_BOM);
  const isBom = (location.pathname.startsWith("/bom") || location.pathname.startsWith("/owner")) && currentRole == ROLE_BOM;
  
  const hasAddPermission = useMemo(() => isCamBom ? masterDataPermission?.add : true, [masterDataPermission]);
  const hasDeletePermission = useMemo(() => isCamBom ? masterDataPermission?.delete : true, [masterDataPermission]);
  const hasUpdatePermission = useMemo(() => isCamBom ? masterDataPermission?.update : true, [masterDataPermission]);
  
  const { data: globalPropertyBlocks, isLoading: isLoadingGlobalPropertyBlocks } =
    useGetPropertyBlocksZoneWise({
      enabled: !isBom,
    });

  const { data: bomPropertyBlocks, isLoading: isLoadingBomPropertyBlocks } =
    useGetPropertyBlockByCommunityId({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!(isBom && communityData?.communityId),
      },
    });

  const propertyBlocks = isBom ? bomPropertyBlocks : globalPropertyBlocks;
  const isLoadingPropertyBlocks = isBom ? isLoadingBomPropertyBlocks : isLoadingGlobalPropertyBlocks;

  // Define Table Columns
  const columns = [
    {
      accessorKey: "communityId",
      header: "Community Name",
      accessorFn: (row: any) => row?.communityName || "",
    },
    {
      accessorKey: "zoneName",
      header: "Zone Name",
      accessorFn: (row: any) => String(row?.zoneName || ""),
    },
    {
      accessorKey: "blockCount",
      header: "# Blocks",
      cell: ({row}: any) => <Button type="button" variant="link" className="px-0" onClick={() => {
        setSelectedRow(row.original);
        setOpenBlockDialog(true);
      }}>
        {row?.original?.blockCount || (row?.original?.blockId ? 1 : "")}
      </Button>
    },
    {
      accessorKey: "propertyBlockDtos",
      header: "Block Name",
      accessorFn: (row: any) => String(row?.propertyBlockDtos?.map((data: any) => data?.blockName)?.join(", ") || row?.blockName || ""),
    },
    ...(isOwner
      ? []
      : hasDeletePermission || hasUpdatePermission ? [
          {
            id: "actions",
            header: "Actions",
            cell: ({row}: any) => {
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger>
                    <MoreVertical className="w-5 h-5 cursor-pointer" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="bg-white border-0 shadow-md"
                  >
                    {hasUpdatePermission && <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={() => {
                        navigate(`${row.original.communityId}/${row.original?.zoneId}/${routes.edit}`)
                      }}
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </DropdownMenuItem>}
                    {hasDeletePermission && <DropdownMenuItem
                      className="flex items-center gap-2"
                      onClick={() => {
                        setSelectedRow(row.original);
                        setOpenDeleteDialog(true);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </DropdownMenuItem>}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ] : []),
  ];

  const blockColumns = [
    {
      accessorKey: "blockName",
      header: "Block Name",
      accessorFn: (row: any) => row?.blockName || "",
    },
    {
      accessorKey: "description",
      header: "Description",
      accessorFn: (row: any) => String(row?.description || ""),
    },
  ]

  const { mutate: deleteInfo, isPending: isDeletePropertyBlockPending } =
    useBulkDeletePropertyBlockMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Property Block Deleted Successfully");
        queryClient.invalidateQueries({
          queryKey: ["property-blocks-zone-wise"],
        });
        setOpenDeleteDialog(false);
      },
    });

  const onAdd = () => {
    navigate(`${routes.add}`);
  };

  const onDeleteConfirm = () => {
    deleteInfo({ zoneId: selectedRow?.zoneId, communityId: selectedRow?.communityId });
  };

  return {
    onAdd,
    isMobile,
    globalFilter,
    setGlobalFilter,
    columns,
    propertyBlocks,
    isLoadingPropertyBlocks,
    openDeleteDialog,
    setOpenDeleteDialog,
    selectedRow,
    isDeletePropertyBlockPending,
    onDeleteConfirm,
    isOwner,
    openBlockDialog,
    setOpenBlockDialog,
    blockColumns,
    hasAddPermission
  };
};
export default usePropertyBlock;
